import { UnitCode } from '~/graphql/graphql';
import { round3 } from '~/utils/lengthParser';
import { parseImportNumber } from './numberParse';
import type { CatalogProduct, CellIssue } from './types';

export const TRUCKLOADS = 'truckloads';
export const BUNDLES = 'bundles';
export const UNSTATED = 'unknown';

export const PACK_SIZE = /\s*\((\d+)\)$/;

export type StockSource =
  | { per: typeof TRUCKLOADS; count: number }
  | { per: typeof BUNDLES; count: number; pieces: number }
  | { per: typeof UNSTATED; count: number };

export type StockNote = Pick<CellIssue, 'level' | 'messageKey' | 'params'>;

export function packSizeOf(descriptor: string): number | null {
  const pack = PACK_SIZE.exec(descriptor.trim());
  return pack ? Number(pack[1]) : null;
}

export function stockSourceOf(
  stock: unknown,
  unit: unknown,
  descriptor: unknown
): StockSource | undefined {
  const count = parseImportNumber(stock);
  if (count === null) return undefined;
  const per = String(unit ?? '')
    .trim()
    .toLowerCase();
  if (per === TRUCKLOADS) return { per, count };
  if (per === BUNDLES) {
    const pieces = packSizeOf(String(descriptor ?? ''));
    return pieces === null ? undefined : { per, count, pieces };
  }
  return per === UNSTATED ? { per, count } : undefined;
}

const amountOf = (value: number) => String(round3(value));

export function stockFor(
  source: StockSource,
  product: CatalogProduct
): { stock?: string; unit: string; note: StockNote } {
  const unit = product.standardUnitsCode;
  if (source.per === TRUCKLOADS) {
    if (!product.unitsPerTruckload) {
      return {
        stock: '',
        unit: '',
        note: {
          level: 'error',
          messageKey: 'validation.stock_truckloads_no_capacity',
          params: { count: source.count },
        },
      };
    }
    const stock = amountOf(source.count * product.unitsPerTruckload);
    return {
      stock,
      unit,
      note: {
        level: 'warning',
        messageKey: 'validation.stock_from_truckloads',
        params: { count: source.count, stock, unit },
      },
    };
  }
  if (source.per === BUNDLES) {
    const stock = amountOf(source.count * source.pieces);
    return {
      stock,
      unit: UnitCode.Pcs,
      note: {
        level: 'warning',
        messageKey: 'validation.stock_from_bundles',
        params: { count: source.count, pieces: source.pieces, stock },
      },
    };
  }
  return {
    unit,
    note: {
      level: 'warning',
      messageKey: 'validation.stock_unit_assumed',
      params: { unit },
    },
  };
}
